"use client";

import { useEffect, useSyncExternalStore, useState } from "react";
import Link from "next/link";
import { useTranslations } from "@/i18n/LocaleProvider";

type Choice = "granted" | "denied";
const STORAGE_KEY = "cookie-consent";

function applyChoice(choice: Choice) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push([
    "consent",
    "update",
    {
      analytics_storage: choice,
      ad_storage: choice,
      ad_user_data: choice,
      ad_personalization: choice,
    },
  ]);
}

function subscribe(callback: () => void) {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}

function getSnapshot(): Choice | null {
  return localStorage.getItem(STORAGE_KEY) as Choice | null;
}

function getServerSnapshot(): Choice | null {
  return null;
}

export default function CookieConsent() {
  // Reads localStorage synchronously during render rather than in an effect -- avoids both
  // the hydration mismatch a plain useState initializer would cause (server has no
  // localStorage) and the setState-in-effect anti-pattern a useEffect-based read would be.
  const { t } = useTranslations();
  const stored = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (stored) applyChoice(stored);
  }, [stored]);

  function choose(choice: Choice) {
    localStorage.setItem(STORAGE_KEY, choice);
    applyChoice(choice);
    setDismissed(true);
  }

  if (dismissed || stored) return null;

  return (
    <div className="cookie-consent-banner fixed inset-x-0 bottom-0 z-50 border-t border-foreground/10 bg-background px-6 py-4 shadow-lg">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-3 sm:flex-row">
        <p className="text-sm text-foreground/80">
          {t("cookieConsent.notice")}{" "}
          <Link href="/privacy" className="underline hover:text-accent">
            {t("cookieConsent.privacyPolicy")}
          </Link>
          .
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => choose("denied")}
            className="rounded-full border border-foreground/20 px-4 py-2 text-sm font-medium"
          >
            {t("cookieConsent.decline")}
          </button>
          <button
            onClick={() => choose("granted")}
            className="rounded-full bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground"
          >
            {t("cookieConsent.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
